import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "@/components/ui/sonner";

const CartContext = createContext(null);

const load = () => {
  try {
    return JSON.parse(localStorage.getItem("classina_cart")) || [];
  } catch {
    return [];
  }
};

export const itemId = (slug, variant) => `${slug}|${variant || ""}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);

  useEffect(() => {
    localStorage.setItem("classina_cart", JSON.stringify(items));
  }, [items]);

  const add = (product, qty = 1, variant = null, pack = null) => {
    const id = itemId(product.slug, [variant?.name, pack?.label].filter(Boolean).join("|"));
    setItems((prev) => {
      const existing = prev.find((i) => i.id === id);
      if (existing) {
        return prev.map((i) => (i.id === id ? { ...i, qty: Math.min(i.qty + qty, 500) } : i));
      }
      return [
        ...prev,
        {
          id,
          slug: product.slug,
          variant: variant?.name || null,
          pack: pack?.label || null,
          pieces: pack?.pieces || 1,
          name: product.name,
          price: pack?.price ?? product.price,
          mrp: (product.mrp || product.price) * (pack?.pieces || 1),
          image: variant?.image || product.image,
          min_qty: product.min_qty || 1,
          qty,
        },
      ];
    });
    toast.success(`${product.name}${variant ? ` (${variant.name})` : ""}${pack ? ` [${pack.label}]` : ""} added to cart`);
  };

  const setQty = (id, qty) => {
    setItems((prev) => (qty <= 0 ? prev.filter((i) => i.id !== id) : prev.map((i) => (i.id === id ? { ...i, qty } : i))));
  };

  const remove = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const clear = () => setItems([]);

  const { subtotal, count } = useMemo(
    () => ({
      subtotal: items.reduce((s, i) => s + i.price * i.qty, 0),
      count: items.reduce((s, i) => s + i.qty, 0),
    }),
    [items],
  );

  return (
    <CartContext.Provider value={{ items, add, setQty, remove, clear, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
